<template>
  <div class="content-stacked-cards">
    <div ref="scrollcontainerRef" class="scroll-container">
      <template v-for="card in data.cardContent" :key="card.id">
        <component
          class="card"
          :is="card.link ? linkComponent : 'div'"
          :link="card.link ?? undefined"
        >
          <div class="logo">
            <img
              class="image"
              v-if="card.asset && card.asset.url"
              :src="card.asset.url"
            />
          </div>
          <div class="text text-sm-vl text-2xs-vs">{{ card.text }}</div>
        </component>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import ScrollTrigger from 'gsap/ScrollTrigger';
import gsap from 'gsap';

import type { ContentStackedCardFragment } from '#gql';

defineProps<{
  data: ContentStackedCardFragment;
}>();

const linkComponent = defineAsyncComponent(
  () => import('~/components/partials/Link.vue'),
);

const scrollcontainerRef = ref<HTMLElement | null>(null);

onMounted(() => {
  nextTick(() => {
    const cards = gsap.utils.toArray('.card');
    cards.forEach((card, index) => {
      const isLastCard = index === cards.length - 1;
      const tween = gsap.to(card as HTMLElement, {
        scrollTrigger: {
          trigger: card as HTMLElement,
          start: () => `top-=120`,
          end: () => `bottom`,
          scrub: 0.4,
          invalidateOnRefresh: true,
        },
        ease: 'none',
        scale: () => 1 - (cards.length - index) * 0.06,
        opacity: 0,
      });

      ScrollTrigger.create({
        trigger: card as HTMLElement,
        start: 'top-=120 top',
        pin: !isLastCard,
        pinSpacing: false,
        id: 'pin',
        end: 'max',
        invalidateOnRefresh: true,
      });
    });

    setTimeout(() => {
      ScrollTrigger.refresh();
    }, 200);
  });
});
</script>

<style lang="scss">
.content-stacked-cards {
  .scroll-container {
    overflow: visible;
    display: flex;
    flex-direction: column;
    gap: 2.5rem;

    a.card {
      // Reset link styles that interfere with gsap animations
      transition: none;
      text-decoration: none;
    }

    .card {
      height: fit-content;
      background: #f6fffe;
      border-radius: 0.5rem;
      padding: 3.5rem 3rem;
      display: grid;
      grid-template-columns: 3fr 4fr;
      grid-template-areas:
        'a c'
        'b c';
      column-gap: 2rem;

      @media (--vs) {
        grid-template-areas:
          'a a'
          'c c';
        column-gap: 1rem;
        row-gap: 2rem;
        padding: 2rem 1.5rem;
      }

      .logo {
        height: 4.75rem;
        grid-area: a;
        .image {
          height: 100%;

          :deep(img) {
            object-fit: contain;
            object-position: center left;
          }
        }
        @media (--vs) {
          width: 11.25rem;
        }
      }

      .title {
        grid-area: b;
      }

      .text {
        grid-area: c;
      }
    }
  }
}
</style>
